import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { formatApiError } from '@mustauto/utils';

/**
 * Terceiro estado de toda tela com busca (Regra 11): carregando / vazio / erro.
 *
 * Existia só como cópia local no web-admin — o kit exportava `EmptyState` e
 * `Skeleton` e parava aí, então partner e backoffice não tinham nenhum, e quinze
 * telas do próprio admin caíam no vazio quando a busca falhava (galeria
 * "Nenhum item encontrado" com o estoque cheio). Subiu para cá como estava.
 *
 * O detalhe vem do `formatApiError`, que separa falta de conexão de erro do
 * servidor. Sem ele o retry vira aposta: quem está sem rede clica dez vezes.
 */
interface ErrorStateProps {
  /** Optional custom title. Defaults to a generic pt-BR load-failure message. */
  title?: string;
  /**
   * Raw error (e.g. from useQuery). When provided, a short detail line is
   * rendered via formatApiError so the user knows if it's connectivity vs server.
   */
  error?: unknown;
  /** Override the detail line directly (takes precedence over `error`). */
  detail?: ReactNode;
  /** Retry handler — wire to the query's refetch. */
  onRetry?: () => void;
}

export function ErrorState({ title, error, detail, onRetry }: ErrorStateProps) {
  const resolvedTitle = title ?? 'Não foi possível carregar os dados';
  const resolvedDetail =
    detail ?? (error !== undefined ? formatApiError(error, 'Ocorreu um erro inesperado.') : undefined);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center justify-center py-16"
      role="alert"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle size={28} />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-foreground">{resolvedTitle}</h3>
      {resolvedDetail && (
        <p className="mt-1 max-w-sm text-center text-sm text-muted-foreground">{resolvedDetail}</p>
      )}
      {onRetry && (
        <div className="mt-5">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            <RotateCw size={16} />
            Tentar novamente
          </button>
        </div>
      )}
    </motion.div>
  );
}
